import { Controller } from "@hotwired/stimulus"

// Corner Geometry Workbench live-link (Phase G1) — the single owner of
// selection / hover / view across the four surfaces:
//
//   hero map ↔ corner list ↔ timeline (spans) ↔ geometry strip
//
// This controller wraps the whole workspace and is the ONLY place that holds
// "which corner is selected / hovered" and "what distance window is in view".
// Every surface reports user intent UP to here via bubbling events; this
// controller fans the resulting state back OUT by calling each surface's public
// API. The surfaces never talk to each other directly.
//
// State (live, ephemeral — NOT persisted; it's glance-level highlight):
//   • this._selectedNumber  — corner number lit everywhere, or null
//   • this._hoveredNumber   — corner number hovered everywhere, or null
//   • this._view            — shared distance window {a,b}, or null (full lap)
//
// Outlets (see _track_detail.html.erb):
//   • track-map-hero → #track_hero_map_canvas — setSelected / highlight /
//     clearHighlight / setHoverM / setView / updateCorners
//   • corner-timeline → #corner_timeline — setSelected / setHovered / setView
//
// Inbound events (all bubble to this wrapping element; wired in the view):
//   List   rowHover/rowLeave            → _hover(n)/_hover(null)
//          rowSelect                    → _select(toggle)
//   Map    track-map-hero:cornerHover   → mapHover   → _hover(n)
//          track-map-hero:cornerLeave   → mapLeave   → _hover(null)
//          track-map-hero:cornerSelect  → mapSelect  → _select(toggle)
//          track-map-hero:viewChange    → mapViewChange   → timeline.setView
//   Tline  corner-timeline:cornerSelect → timelineSelect  → _select(n)
//          corner-timeline:cornerHover  → timelineHover   → _hover(n)
//          corner-timeline:scrub        → timelineScrub   → map.setHoverM(m)
//          corner-timeline:viewChange   → timelineViewChange → map.setView
//          corner-timeline:cornerEdit   → timelineEdit    → map.updateCorners
//          corner-timeline:cornerCommit → timelineCommit  → no-op (G2 re-sync is
//                                          driven by the corners-card reconnect)
//
// ── G2: commit consistency (the one re-sync) ─────────────────────────────────
// Every PERSISTED mutation (handle release, geometry field, Magic, Fit, Remove,
// rename, add, seed) returns the same card_stream Turbo Stream, which replaces
// ONLY #track_corners_card (the list). The hero map + timeline are NOT in the
// stream — swapping the map element churns its outlet, races its connect(), and
// drops the live selection/zoom. Instead, the replaced card carries the fresh
// corners_geometry_json (on its corner-editor controller); its outlet reconnect
// fires cornerEditorOutletConnected → _resync, which updates the map + timeline
// IN PLACE (map.updateCorners / timeline.adopt, both selection/zoom-preserving)
// and re-applies the selection to the freshly-replaced list row. One trigger,
// no element churn.
//
// Everything is ADDITIVE and defensive: a missing outlet (outline-less track has
// no map) or a missing row must not break the others — the list + timeline still
// work when the map is absent.
export default class extends Controller {
  static outlets = [
    "track-map-hero",
    "corner-timeline",
    "corner-editor",
    "section-editor",
  ]
  static targets = ["cornersPane", "sectionsPane", "modeButton"]

  // Transient hover styling mirrored onto a list row (selection uses
  // aria-selected, driven by the row's own Tailwind aria-selected: classes).
  static HOVER_CLASSES = ["bg-app-surface-hover"]

  // localStorage key for the Corners ⇄ Sections mode. Persisted so the mode
  // survives the corners-card Turbo replace (every persisted mutation replaces
  // #track_corners_card, which re-runs connect() here).
  static MODE_KEY = "cw:mode"

  connect() {
    this._selectedNumber = null
    this._hoveredNumber = null
    this._selectedSectionId = null
    this._hoveredSectionId = null
    this._view = null

    // Restore the persisted mode and re-apply it. This runs on every (re)connect
    // — including after a card_stream replace re-renders the workspace — so the
    // visible pane + active segment never snap back to the server default.
    const stored = localStorage.getItem(this.constructor.MODE_KEY)
    this._applyMode(stored === "sections" ? "sections" : "corners")
  }

  // ── Corners ⇄ Sections mode ─────────────────────────────────────────────────

  // Bound to the segmented control (data-mode="corners"|"sections").
  setMode(event) {
    const mode = event.currentTarget?.dataset?.mode
    if (mode !== "corners" && mode !== "sections") return
    this._applyMode(mode)
  }

  // Single source of truth for the mode. Persists it, swaps which list pane is
  // visible, reflects the active segment, and tells the timeline + map to render
  // their layer for this mode. Idempotent + defensive (missing outlet/target on
  // an outline-less track must not throw).
  _applyMode(mode) {
    this._mode = mode
    localStorage.setItem(this.constructor.MODE_KEY, mode)

    // Toggle the two stacked list panes (default corners visible).
    this.cornersPaneTargets.forEach((el) =>
      el.classList.toggle("hidden", mode !== "corners"),
    )
    this.sectionsPaneTargets.forEach((el) =>
      el.classList.toggle("hidden", mode !== "sections"),
    )

    // Reflect the active segment on the toggle.
    this.modeButtonTargets.forEach((btn) => {
      const active = btn.dataset.mode === mode
      btn.classList.toggle("cw-mode-seg--active", active)
      btn.setAttribute("aria-pressed", active ? "true" : "false")
    })

    // Fan the mode out to the timeline + map (guarded — they fill the real
    // section rendering in Phases C/E; here it's the corner-layer ghost/show).
    if (
      this.hasCornerTimelineOutlet &&
      typeof this.cornerTimelineOutlet.setMode === "function"
    ) {
      this.cornerTimelineOutlet.setMode(mode)
    }
    if (
      this.hasTrackMapHeroOutlet &&
      typeof this.trackMapHeroOutlet.setMode === "function"
    ) {
      this.trackMapHeroOutlet.setMode(mode)
    }

    // Phase C/E: when Sections mode is active, feed the current section tiling to
    // BOTH the timeline (bands + strip) and the map (band-arcs). The corners array
    // each surface needs for ghosted diamonds / contained-corner chips is already
    // held by them (set on connect / adopt), so only sections need pushing here.
    if (mode === "sections") this._pushSections()
  }

  // Read the fresh sections payload off the section-editor outlet (the card
  // carries data-section-editor-sections-value) and push it into the timeline +
  // the map band-arc layer. Defensive: a missing outlet (outline-less track) or
  // an absent setSections (older build) must not throw.
  _pushSections() {
    const sections =
      this.hasSectionEditorOutlet &&
      Array.isArray(this.sectionEditorOutlet.sectionsValue)
        ? this.sectionEditorOutlet.sectionsValue
        : []
    this._fanSections(sections)
  }

  // Fan a section array out to the timeline + map (the one place both surfaces'
  // setSections is called, so a live-drag preview and a committed re-sync share
  // a path). Each call is independently guarded.
  _fanSections(sections) {
    const arr = Array.isArray(sections) ? sections : []
    if (
      this.hasCornerTimelineOutlet &&
      typeof this.cornerTimelineOutlet.setSections === "function"
    ) {
      this.cornerTimelineOutlet.setSections(arr)
    }
    if (
      this.hasTrackMapHeroOutlet &&
      typeof this.trackMapHeroOutlet.setSections === "function"
    ) {
      this.trackMapHeroOutlet.setSections(arr)
    }
  }

  // ── Core state transitions (single source of truth) ─────────────────────────

  // Select a corner everywhere (or clear with null). Lights: the map (arc +
  // chevrons + racing line), the timeline (span + handles), the geometry strip
  // (rendered by timeline.setSelected), and the list row (aria-selected wash +
  // left bar). Re-applying the same selection is idempotent.
  _select(number) {
    const n = number == null ? null : Number(number)
    this._selectedNumber = n

    if (this.hasTrackMapHeroOutlet) this.trackMapHeroOutlet.setSelected(n)
    if (this.hasCornerTimelineOutlet) this.cornerTimelineOutlet.setSelected(n)
    this._syncRowSelection(n)
  }

  // Hover a corner everywhere (or clear with null). Transient: must NOT change
  // selection. Glows the map marker, the timeline span, and the list row.
  _hover(number) {
    const n = number == null ? null : Number(number)
    if (this._hoveredNumber === n) return

    // Drop the hover class off the previously-hovered row.
    this._row(this._hoveredNumber)?.classList.remove(
      ...this.constructor.HOVER_CLASSES,
    )
    this._hoveredNumber = n

    if (this.hasTrackMapHeroOutlet) {
      if (n == null) this.trackMapHeroOutlet.clearHighlight()
      else this.trackMapHeroOutlet.highlight(n)
    }
    if (this.hasCornerTimelineOutlet) this.cornerTimelineOutlet.setHovered(n)
    this._row(n)?.classList.add(...this.constructor.HOVER_CLASSES)
  }

  // Set aria-selected on the matching row, clearing it on every other row.
  _syncRowSelection(number) {
    const rows = this.element.querySelectorAll(".corner-row")
    rows.forEach((row) => {
      const match =
        number != null && Number(row.dataset.cornerNumber) === number
      row.setAttribute("aria-selected", match ? "true" : "false")
      if (match) row.scrollIntoView?.({ block: "nearest" })
    })
  }

  // ── Section selection (single source of truth) ──────────────────────────────
  //
  // The section list rows carry the same selection/hover affordances as corner
  // rows (data-section-id/-number + aria-selected). Selecting a section lights it
  // everywhere: the map band-arc (setSelectedSection), the timeline band + strip
  // (setSelectedSection), and the list row (aria-selected). All three section
  // entry points (list row, timeline band, map arc) route through _selectSection
  // so the surfaces never disagree.

  sectionRowHover(event) {
    this._hoverSection(this.#sectionIdFrom(event))
  }

  sectionRowLeave() {
    this._hoverSection(null)
  }

  // Bound to the whole row: a click anywhere on it selects (re-clicking the lit
  // row toggles it off, matching corner rows). A click that lands in the name
  // <input>/form only ever SELECTS — never toggles — so editing the name can't
  // accidentally deselect the section out from under you.
  sectionRowSelect(event) {
    const id = this.#sectionIdFrom(event)
    if (id == null) return
    if (event.target.closest("input, textarea, [contenteditable], form")) {
      if (this._selectedSectionId !== id) this._selectSection(id)
      return
    }
    this._selectSection(this._selectedSectionId === id ? null : id)
  }

  // ── Section hover (single source of truth) ──────────────────────────────────
  //
  // Hover a section everywhere (or clear with null). Transient — must NOT change
  // selection (mirrors the corner `_hover`): it glows the map band-arc, the
  // timeline band + title rail, and the list row, all at "hover" emphasis (idle <
  // hover < selected). All three entry points (list row, timeline band, map arc)
  // route through here so the surfaces never disagree. `id` is normalised to a
  // string (the section db id).
  _hoverSection(id) {
    const next = id == null ? null : String(id)
    if (this._hoveredSectionId === next) return

    // Drop the hover class off the previously-hovered row.
    this.#sectionRow(this._hoveredSectionId)?.classList.remove(
      ...this.constructor.HOVER_CLASSES,
    )
    this._hoveredSectionId = next

    if (
      this.hasTrackMapHeroOutlet &&
      typeof this.trackMapHeroOutlet.setHoveredSection === "function"
    ) {
      this.trackMapHeroOutlet.setHoveredSection(next)
    }
    if (
      this.hasCornerTimelineOutlet &&
      typeof this.cornerTimelineOutlet.setHoveredSection === "function"
    ) {
      this.cornerTimelineOutlet.setHoveredSection(next)
    }
    this.#sectionRow(next)?.classList.add(...this.constructor.HOVER_CLASSES)
  }

  // Select a section everywhere (or clear with null). Idempotent. `id` is
  // normalised to a string (the section db id) — both the map + timeline compare
  // section ids as strings.
  _selectSection(id) {
    const next = id == null ? null : String(id)
    this._selectedSectionId = next

    if (
      this.hasTrackMapHeroOutlet &&
      typeof this.trackMapHeroOutlet.setSelectedSection === "function"
    ) {
      this.trackMapHeroOutlet.setSelectedSection(next)
    }
    if (
      this.hasCornerTimelineOutlet &&
      typeof this.cornerTimelineOutlet.setSelectedSection === "function"
    ) {
      this.cornerTimelineOutlet.setSelectedSection(next)
    }
    this._syncSectionRowSelection(next)
  }

  _syncSectionRowSelection(id) {
    this.element.querySelectorAll(".section-row").forEach((row) => {
      const match = id != null && row.dataset.sectionId === String(id)
      row.setAttribute("aria-selected", match ? "true" : "false")
      if (match) row.scrollIntoView?.({ block: "nearest" })
    })
  }

  // ── List row → state ────────────────────────────────────────────────────────

  rowHover(event) {
    this._hover(this.#numberFrom(event))
  }

  rowLeave() {
    this._hover(null)
  }

  // Click toggles: re-selecting the lit corner clears selection (matches the
  // prototype's onSelect(selected ? null : id)).
  rowSelect(event) {
    const n = this.#numberFrom(event)
    if (n == null) return
    // Bound to the whole row: a click in the name <input>/form only SELECTS
    // (never toggles) so editing the name can't deselect the corner. Mirrors
    // sectionRowSelect.
    if (event.target.closest("input, textarea, [contenteditable], form")) {
      if (this._selectedNumber !== n) this._select(n)
      return
    }
    this._select(this._selectedNumber === n ? null : n)
  }

  // ── Map → state ───────────────────────────────────────────────────────────

  mapHover(event) {
    this._hover(event.detail?.number)
  }

  mapLeave() {
    this._hover(null)
  }

  mapSelect(event) {
    const n = event.detail?.number
    if (n == null) {
      this._select(null)
      return
    }
    const num = Number(n)
    this._select(this._selectedNumber === num ? null : num)
  }

  mapViewChange(event) {
    this._applyView(event.detail?.view, "map")
  }

  // ── Timeline → state ────────────────────────────────────────────────────────

  timelineSelect(event) {
    // The timeline already toggles its own selection (a span-rail click selects;
    // an empty-track click emits {number:null}); mirror its decision verbatim so
    // the map + list match — do NOT re-toggle here or it would invert.
    this._select(event.detail?.number)
  }

  timelineHover(event) {
    this._hover(event.detail?.number)
  }

  // Scrub → drive the map's live position dot.
  timelineScrub(event) {
    if (this.hasTrackMapHeroOutlet)
      this.trackMapHeroOutlet.setHoverM(event.detail?.m)
  }

  timelineViewChange(event) {
    this._applyView(event.detail?.view, "timeline")
  }

  // Live handle/rail drag — push the working-corner array to the map so its
  // arc/marker move in lockstep with the timeline span during the drag.
  timelineEdit(event) {
    const corners = event.detail?.corners
    if (this.hasTrackMapHeroOutlet && Array.isArray(corners)) {
      this.trackMapHeroOutlet.updateCorners(corners)
    }
  }

  // After a persisted mutation the canonical re-sync is driven by the corners-card
  // reconnect (cornerEditorOutletConnected → _resync) once the card_stream Turbo
  // Stream replaces #track_corners_card. This handler is a harmless no-op kept for
  // the view's data-action binding; the timeline no longer emits cornerCommit on a
  // committed mutation.
  timelineCommit() {}

  // ── Timeline (Sections layer) → state (Phase E) ─────────────────────────────

  // Live boundary drag — the detail carries the working section tiling. Push it
  // straight into the map's band-arc layer so the arcs rebalance in lockstep with
  // the timeline bands as the boundary moves (the satisfying live preview). The
  // timeline is mid-drag and owns its own copy, so only the map is fed here.
  timelineSectionEdit(event) {
    const sections = event.detail?.sections
    if (
      this.hasTrackMapHeroOutlet &&
      Array.isArray(sections) &&
      typeof this.trackMapHeroOutlet.setSections === "function"
    ) {
      this.trackMapHeroOutlet.setSections(sections)
    }
  }

  // A band was selected on the timeline. Route through the shared selection path
  // so the map arc + list row light too (the timeline already lit its own band +
  // strip, but setSelectedSection there is idempotent).
  timelineSectionSelect(event) {
    this._selectSection(event.detail?.id ?? null)
  }

  // A band was hovered/left on the timeline. Route through the shared hover path so
  // the map arc + list row glow too (the timeline already lit its own band, but
  // setHoveredSection there is idempotent). Hover never changes selection.
  timelineSectionHover(event) {
    this._hoverSection(event.detail?.id ?? null)
  }

  timelineSectionLeave() {
    this._hoverSection(null)
  }

  // ── Map (Sections layer) → state (Phase E) ──────────────────────────────────

  // A band-arc was clicked on the map. Toggle like the list rows (re-clicking the
  // lit section clears it) and fan the selection out everywhere.
  mapSectionSelect(event) {
    const id = event.detail?.id == null ? null : String(event.detail.id)
    if (id == null) {
      this._selectSection(null)
      return
    }
    this._selectSection(this._selectedSectionId === id ? null : id)
  }

  // A band-arc was hovered/left on the map. Route through the shared hover path so
  // the timeline band + list row glow too (the map already lit its own arc, but
  // setHoveredSection there is idempotent). Hover never changes selection.
  mapSectionHover(event) {
    this._hoverSection(event.detail?.id ?? null)
  }

  mapSectionLeave() {
    this._hoverSection(null)
  }

  // ── G2: re-sync on corners-card reconnect ───────────────────────────────────
  //
  // The corners card (#track_corners_card) is the ONE element a persisted mutation
  // replaces (card_stream). It carries the fresh corners_geometry_json on its
  // corner-editor controller, so its reconnect is our re-sync trigger AND data
  // source — no map element churn, and reading a Stimulus value needs no map
  // render state. Skip the very first connect (initial server render is already
  // current) and defer one frame so the replaced card's new rows are fully in the
  // DOM before _syncRowSelection runs.
  cornerEditorOutletConnected(editor) {
    // Skip the initial connect (the server render is already current); re-sync on
    // every later one (each is a card_stream replace). The map element is NOT
    // swapped, so there's no connect()-race to wait out — the new card + its rows
    // are already in the DOM when this fires, so we re-sync synchronously.
    if (!this._cardConnectedOnce) {
      this._cardConnectedOnce = true
      return
    }
    this._resync(editor)
  }

  // When the timeline (re)connects — including after a corners-card replace re-runs
  // its tree — re-push the current mode + section tiling so its overlay matches the
  // workbench state regardless of connect ordering vs. this controller.
  cornerTimelineOutletConnected(timeline) {
    if (this._mode && typeof timeline.setMode === "function")
      timeline.setMode(this._mode)
    if (this._mode === "sections") this._pushSections()
  }

  // When the hero map (re)connects — it can outlive a corners-card replace but
  // its element is also rebuilt when the whole _hero_map partial is re-rendered
  // (a corner-mutation Turbo Stream) — re-push the current mode + section tiling
  // so its corner-ghost + band-arc layers match the workbench state regardless of
  // connect ordering vs. this controller.
  trackMapHeroOutletConnected(map) {
    if (this._mode && typeof map.setMode === "function") map.setMode(this._mode)
    if (this._mode === "sections" && typeof map.setSections === "function") {
      const sections =
        this.hasSectionEditorOutlet &&
        Array.isArray(this.sectionEditorOutlet.sectionsValue)
          ? this.sectionEditorOutlet.sectionsValue
          : []
      map.setSections(sections)
      if (
        this._selectedSectionId != null &&
        typeof map.setSelectedSection === "function"
      ) {
        map.setSelectedSection(this._selectedSectionId)
      }
    }
  }

  // ── Section-card reconnect re-sync (Phase C) ────────────────────────────────
  //
  // Every persisted section mutation (boundary/split/merge/rename/destroy/seed)
  // returns a card_stream replacing ONLY #track_sections_card — the timeline + map
  // are NOT in the stream (mirrors the corners G2 decision). The replaced card
  // carries the fresh sections_json on its section-editor controller, so its
  // reconnect is our re-sync trigger AND data source: push the committed (clamped +
  // renumbered) tiling into the timeline + map band-arcs IN PLACE so the bands +
  // strip settle. Only act while Sections mode is showing (a corner-mode commit can
  // also re-run this on the initial connect chain). Skip the first connect (initial
  // render is already current).
  sectionEditorOutletConnected(editor) {
    if (!this._sectionCardConnectedOnce) {
      this._sectionCardConnectedOnce = true
      return
    }
    if (this._mode !== "sections") return
    this._pushSections()

    // Selection survival: setSections drops a selection whose id vanished (merge/
    // destroy); if it survived, re-light it across the surfaces + the freshly
    // replaced list row. Re-applying the same id is idempotent.
    const sections = Array.isArray(editor?.sectionsValue)
      ? editor.sectionsValue
      : []
    const sel = this._selectedSectionId
    const present =
      sel != null && sections.some((s) => String(s.id) === String(sel))
    this._selectSection(present ? sel : null)
  }

  _resync(editor) {
    const corners = Array.isArray(editor?.cornersValue)
      ? editor.cornersValue
      : []

    // Update the map IN PLACE (the element is never replaced, so its selection +
    // zoom + rotation survive); updateCorners re-reads geometry + names + source.
    if (this.hasTrackMapHeroOutlet)
      this.trackMapHeroOutlet.updateCorners(corners)
    // Re-seed the timeline's working copy + re-render its spans/labels/strip,
    // preserving its current zoom (its instance is never replaced).
    if (this.hasCornerTimelineOutlet) this.cornerTimelineOutlet.adopt(corners)

    // Selection survival: if the selected number no longer exists (removed, or a
    // Fit/renumber dropped it), clear selection; otherwise re-light it (map +
    // timeline + the geometry strip keep it through the calls above; the
    // freshly-replaced list rows need aria-selected re-applied). Apply selection
    // to the map/timeline AND re-stamp the new rows in one shot.
    const sel = this._selectedNumber
    const present =
      sel != null && corners.some((c) => Number(c.number) === Number(sel))
    this._select(present ? sel : null)
  }

  // ── View sync ───────────────────────────────────────────────────────────────

  // Apply a view window from one surface to the OTHER. setView on either surface
  // does NOT re-emit viewChange, so there's no feedback loop; we still guard
  // against redundant churn when the window is unchanged.
  _applyView(view, source) {
    const next = this.#normalizeView(view)
    if (this.#viewEqual(next, this._view)) return
    this._view = next

    if (source === "map") {
      if (this.hasCornerTimelineOutlet) this.cornerTimelineOutlet.setView(next)
    } else {
      if (this.hasTrackMapHeroOutlet) this.trackMapHeroOutlet.setView(next)
    }
  }

  // ── helpers ────────────────────────────────────────────────────────────────

  #normalizeView(view) {
    if (!view || typeof view.a !== "number" || typeof view.b !== "number")
      return null
    return { a: view.a, b: view.b }
  }

  #viewEqual(a, b) {
    if (a == null && b == null) return true
    if (a == null || b == null) return false
    return Math.abs(a.a - b.a) < 0.5 && Math.abs(a.b - b.b) < 0.5
  }

  #numberFrom(event) {
    const el = event.currentTarget?.closest("[data-corner-number]")
    if (!el) return null
    const n = Number(el.dataset.cornerNumber)
    return Number.isNaN(n) ? null : n
  }

  #sectionIdFrom(event) {
    return (
      event.currentTarget?.closest("[data-section-id]")?.dataset.sectionId ??
      null
    )
  }

  #sectionRow(id) {
    if (id == null) return null
    return this.element.querySelector(
      `#track_sections_card [data-section-id="${id}"]`,
    )
  }
}
